import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {getConfig} from './config'
import {IbeConfig} from '../schema'
import {booking} from '@whyhotel/common-schema'
import {formatISODate, toLocalDateTime} from '../services/dateUtils'
import {RootState} from '../store/store'
import {selectSessionId} from '../store/slices/sessionSlice'

const {ibeApiUrl, ibeConfigCached} = getConfig()

export const ibeApi = createApi({
  reducerPath: 'ibeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: ibeApiUrl,
    prepareHeaders: (headers, {getState}) => {
      // adds ibe session ID to headers if available
      const state = getState() as RootState
      const sessionId = selectSessionId(state)
      if (sessionId) {
        headers.set('x-ibe-session-id', sessionId)
      }
      return headers
    }
  }),
  tagTypes: [
    'BookingConfirmation',
    'Config',
    'LatestOffers',
    'Offers',
    'ServiceOffers'
  ],
  endpoints: builder => ({
    getConfig: builder.query<IbeConfig, void>({
      query() {
        return `/config?cache=${ibeConfigCached}`
      },
      providesTags: ['Config'],
      keepUnusedDataFor: 120000
    }),
    getLatestOffers: builder.query<
      booking.BookingOffer[],
      booking.BulkRatePlanOfferRequest
    >({
      query(params) {
        return {
          url: `/rate-plan-offers`,
          method: 'POST',
          body: params
        }
      },
      providesTags: ['LatestOffers']
    }),
    getBookingConfirmation: builder.query<booking.BookingConfirmation, string>({
      query(paymentSessionId: string) {
        return {
          url: `/booking-confirmation?paymentSessionId=${paymentSessionId}`,
          method: 'GET'
        }
      },
      providesTags: ['BookingConfirmation']
    }),
    // searchOffers: builder.query({
    //   query(params: OfferRequest) {
    //     const dateRange = params.dateRange
    //     const startDate = parseISO(dateRange.start as string)
    //     const endDate = parseISO(dateRange.end as string)
    //     return {
    //       url: `/offers`,
    //       method: 'POST',
    //       body: {
    //         ...params,
    //         dateRange: {
    //           ...dateRange,
    //           start: format(startDate, 'yyyy-MM-dd'),
    //           end: format(endDate, 'yyyy-MM-dd')
    //         }
    //       }
    //     }
    //   },
    //   providesTags: ['Offers']
    // }),
    searchOffers: builder.query<
      booking.UnitGroupOfferResult,
      booking.OfferRequest
    >({
      query(params: booking.OfferRequest) {
        const dateRange = params.dateRange
        return {
          url: `/offersV2`,
          method: 'POST',
          body: {
            ...params,
            dateRange: {
              ...dateRange,
              start: formatISODate(toLocalDateTime(dateRange.start)),
              end: formatISODate(toLocalDateTime(dateRange.end))
            }
          }
        }
      },
      providesTags: ['Offers']
    }),
    searchServiceOffers: builder.query<
      booking.ServiceOfferResults,
      booking.ServiceOfferRequest
    >({
      query(params: booking.ServiceOfferRequest) {
        const dateRange = params.dateRange
        return {
          url: `/service-offers`,
          method: 'POST',
          body: {
            ...params,
            dateRange: {
              ...dateRange,
              start: formatISODate(toLocalDateTime(dateRange.start)),
              end: formatISODate(toLocalDateTime(dateRange.end))
            }
          }
        }
      },
      providesTags: ['ServiceOffers']
    })
  })
})

export const {
  useGetConfigQuery,
  useSearchOffersQuery,
  useLazyGetBookingConfirmationQuery,
  useLazyGetLatestOffersQuery,
  useLazySearchOffersQuery,
  useSearchServiceOffersQuery
} = ibeApi
